import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../context/AuthContext';
import { useGestaoKPIs } from '../../../modules/gestao/hooks/useGestaoKPIs';
import { useGestaoTasks } from '../../../modules/gestao/hooks/useGestaoTasks';
import { useMyTasks } from '../../../modules/minha-mesa/hooks/useMyTasks';
import { useExecutionList } from '../../../modules/execution/hooks/useExecutionList';
import {
  getOperationalSlaTrend,
  getOperationalThroughputTrend,
} from '../../../services/workflowService';
import {
  getFinancialImpactTrend,
  getLeadTimeTrend,
  getStageWipHeatmap,
} from '../../../services/capacidadeService';
import { getPauseReasonPareto } from '../../../services/opsKaizenService';
import GainsMonitorService from '../../../services/gainsMonitorService';
import { supabase } from '../../../lib/supabaseClient';
import {
  buildAnnualSavingsByProject,
  buildGainsRanking,
  buildMonthlyEvolution,
  buildRegisteredBenefits,
  buildSavingsByArea,
  buildSavingsByProject,
  buildWaterfallSteps,
  governBenefits,
} from './opsFinancialDerivations';
import { buildZeroDaySeries, hasSeriesActivity } from './opsKaizenShared';
import type { TaskFilters } from '../../../modules/shared/types/task';

const BASE_TASK_FILTERS: TaskFilters = {
  status: ['pending', 'in_progress'],
  priority: [],
  search: '',
  overdue: false,
};

export interface UseOpsKaizenDataParams {
  organizationId: string;
  periodDays: number;
}

export function useOpsKaizenData({ organizationId, periodDays }: UseOpsKaizenDataParams) {
  const { user } = useAuth();
  const userId = user?.id;
  const chartDays = Math.max(periodDays, 7);
  const zeroDaySeries = useMemo(() => buildZeroDaySeries(chartDays), [chartDays]);

  const { metrics, usersPerformance, stagePerformance } = useGestaoKPIs({ organizationId });

  const taskQuery = useGestaoTasks({
    organizationId,
    filters: BASE_TASK_FILTERS,
    sortField: 'sla_deadline',
    sortDirection: 'asc',
    page: 1,
    pageSize: 150,
  });

  const { tasks: myTasks } = useMyTasks({ userId, organizationId });

  const executionQuery = useExecutionList({
    organizationId,
    filters: {
      status: ['active', 'on_hold'],
      search: '',
      overdue: false,
      priority: [],
    },
    page: 1,
    pageSize: 120,
    sortField: 'started_at',
    sortDirection: 'desc',
  });

  const slaTrendQuery = useQuery({
    queryKey: ['ops-kaizen', 'sla-trend', organizationId, chartDays],
    queryFn: () => getOperationalSlaTrend(organizationId, chartDays),
    staleTime: 60_000,
    enabled: !!organizationId,
  });

  const throughputQuery = useQuery({
    queryKey: ['ops-kaizen', 'throughput', organizationId, chartDays],
    queryFn: () => getOperationalThroughputTrend(organizationId, chartDays),
    staleTime: 60_000,
    enabled: !!organizationId,
  });

  const leadTimeTrendQuery = useQuery({
    queryKey: ['ops-kaizen', 'lead-time', organizationId, chartDays],
    queryFn: () => getLeadTimeTrend(organizationId, chartDays),
    staleTime: 60_000,
    enabled: !!organizationId,
  });

  const stageWipQuery = useQuery({
    queryKey: ['ops-kaizen', 'stage-wip', organizationId],
    queryFn: () => getStageWipHeatmap(organizationId),
    staleTime: 60_000,
    enabled: !!organizationId,
  });

  const pauseParetoQuery = useQuery({
    queryKey: ['ops-kaizen', 'pause-pareto', organizationId, periodDays],
    queryFn: () => getPauseReasonPareto(organizationId, periodDays),
    staleTime: 60_000,
    enabled: !!organizationId,
  });

  const financialImpactQuery = useQuery({
    queryKey: ['ops-kaizen', 'financial-impact', organizationId, periodDays],
    queryFn: () => getFinancialImpactTrend(organizationId, Math.max(periodDays, 14)),
    staleTime: 60_000,
    enabled: !!organizationId,
  });

  const kaizensQuery = useQuery({
    queryKey: ['ops-kaizen', 'kaizens', organizationId],
    queryFn: () => GainsMonitorService.getKaizens({ organization_id: organizationId }),
    staleTime: 30_000,
    enabled: !!organizationId,
  });

  const kaizenRoiQuery = useQuery({
    queryKey: ['ops-kaizen', 'roi-rows', organizationId],
    queryFn: async () => {
      const { data: kaizenIds, error: kaizenError } = await supabase
        .from('kaizen_records')
        .select('id')
        .eq('organization_id', organizationId);
      if (kaizenError) throw kaizenError;
      const ids = (kaizenIds ?? []).map((row) => row.id);
      if (ids.length === 0) return [];
      const { data, error } = await supabase
        .from('kaizen_roi')
        .select('kaizen_id, annual_cost_saved, total_investment')
        .in('kaizen_id', ids);
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 30_000,
    enabled: !!organizationId,
  });

  const slaChartData = slaTrendQuery.data ?? zeroDaySeries;
  const throughputChartData = throughputQuery.data ?? zeroDaySeries;
  const leadTimeChartData = leadTimeTrendQuery.data ?? zeroDaySeries;

  const primaryBottleneck = useMemo(() => {
    const byWip = [...stagePerformance].sort((a, b) => b.avg_wip - a.avg_wip)[0];
    const byLead = [...stagePerformance].sort((a, b) => b.avg_cycle_time_hours - a.avg_cycle_time_hours)[0];
    const top = byWip && byLead
      ? (byWip.avg_wip >= byLead.avg_wip * 0.8 ? byWip : byLead)
      : byWip ?? byLead;
    if (!top || top.avg_wip <= 0) return null;
    const topUser = [...usersPerformance].sort(
      (a, b) => b.avg_wip - a.avg_wip || b.tasks_overdue - a.tasks_overdue,
    )[0];
    const responsibleName = topUser?.user_email?.split('@')[0] ?? null;
    return {
      stageName: top.stage_name,
      wip: Math.round(top.avg_wip),
      leadTimeHours: top.avg_cycle_time_hours,
      slaImpactPct: top.sla_sampled === false ? 0 : Math.max(0, 100 - top.sla_rate),
      slaSampled: top.sla_sampled !== false,
      affectedActions: top.tasks_count || Math.round(top.avg_wip),
      responsibleName,
    };
  }, [stagePerformance, usersPerformance]);

  const bottleneckStageName = primaryBottleneck?.stageName ?? null;

  const operationalStages = useMemo(() => {
    const rows = stagePerformance.slice(0, 6);
    return rows.map((stage) => ({
      name: stage.stage_name,
      wip: stage.avg_wip,
      cycle: `${stage.avg_cycle_time_hours.toFixed(1)}h`,
      leadTimeHours: stage.avg_cycle_time_hours,
      slaRate: stage.sla_rate,
      slaSampled: stage.sla_sampled !== false,
      isBottleneck: stage.stage_name === bottleneckStageName,
      trend: stage.sla_rate >= 95 ? 'up' as const : stage.sla_rate >= 85 ? 'flat' as const : 'down' as const,
    }));
  }, [stagePerformance, bottleneckStageName]);

  const wipStageRows = useMemo(
    () => stagePerformance.filter((stage) => stage.avg_wip > 0).slice(0, 8),
    [stagePerformance],
  );

  const maxStageWip = useMemo(
    () => Math.max(...wipStageRows.map((stage) => stage.avg_wip), 1),
    [wipStageRows],
  );

  const sortedStages = useMemo(
    () => [...stagePerformance].sort((a, b) => b.avg_wip - a.avg_wip),
    [stagePerformance],
  );

  const recurringBottlenecks = useMemo(
    () =>
      stagePerformance
        .map((stage) => ({
          ...stage,
          recurrence_score: Math.round((stage.avg_wip * Math.max(100 - stage.sla_rate, 1)) * 10) / 10,
        }))
        .sort((a, b) => b.recurrence_score - a.recurrence_score)
        .slice(0, 3),
    [stagePerformance],
  );

  const bottleneckChartData = useMemo(() => {
    const source = recurringBottlenecks.length > 0
      ? recurringBottlenecks
      : [{ stage_name: '—', recurrence_score: 0, avg_wip: 0, avg_cycle_time_hours: 0, sla_rate: 0, tasks_count: 0 }];
    return source.map((b) => ({
      ...b,
      va: b.recurrence_score * 0.4,
      nva: b.recurrence_score * 0.6,
    }));
  }, [recurringBottlenecks]);

  const pauseChartData = useMemo(() => {
    if (pauseParetoQuery.data?.length) return pauseParetoQuery.data;
    return [{ reason: '—', count: 0, percentage: 0, cumulativePercentage: 0 }];
  }, [pauseParetoQuery.data]);

  const totalThroughput = throughputChartData.reduce((acc, p) => acc + (p.value ?? 0), 0);

  const financialImpactData = financialImpactQuery.data ?? buildZeroDaySeries(Math.max(periodDays, 14)).map((p) => ({
    ...p,
    economia: 0,
    custo: 0,
  }));

  const teamPendingCount = taskQuery.items.filter((task) => task.status === 'pending').length;
  const teamInProgressCount = taskQuery.items.filter((task) => task.status === 'in_progress').length;
  const teamOverdueCount = taskQuery.items.filter((task) => task.isOverdue).length;

  const overdueExecutions = useMemo(
    () => executionQuery.items.filter((item) => item.isOverdue),
    [executionQuery.items],
  );

  const avgLeadTimeHours = useMemo(() => {
    const active = leadTimeChartData.filter((point) => (point.value ?? 0) > 0);
    if (active.length > 0) {
      return active.reduce((sum, point) => sum + (point.value ?? 0), 0) / active.length;
    }
    const withElapsed = executionQuery.items.filter((item) => typeof item.elapsedMinutes === 'number');
    if (withElapsed.length === 0) return 0;
    const avgMinutes = withElapsed.reduce((acc, item) => acc + (item.elapsedMinutes ?? 0), 0) / withElapsed.length;
    return avgMinutes / 60;
  }, [executionQuery.items, leadTimeChartData]);

  const kaizens = kaizensQuery.data ?? [];

  const projectIds = useMemo(
    () => [...new Set(kaizens.map((kaizen) => kaizen.project_id).filter((id): id is string => Boolean(id)))],
    [kaizens],
  );

  const projectNamesQuery = useQuery({
    queryKey: ['ops-kaizen', 'project-names', organizationId, projectIds],
    enabled: projectIds.length > 0,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('id, nome_projeto')
        .in('id', projectIds);
      if (error) throw error;
      return Object.fromEntries((data ?? []).map((row) => [row.id, row.nome_projeto]));
    },
  });

  const savingsByProject = useMemo(
    () => buildSavingsByProject(kaizens, projectNamesQuery.data ?? {}),
    [kaizens, projectNamesQuery.data],
  );

  const benefitsReady = kaizensQuery.isSuccess && kaizenRoiQuery.isSuccess;
  const benefitLines = useMemo(() => {
    if (!benefitsReady) return [];
    return buildRegisteredBenefits(
      kaizens.map((kaizen) => ({
        id: kaizen.id,
        organizationId: (kaizen as { organization_id?: string | null }).organization_id || organizationId,
        title: kaizen.title,
        type: kaizen.type,
        responsible: kaizen.responsible,
        projectId: kaizen.project_id || null,
        operationalStatus: kaizen.status,
      })),
      kaizenRoiQuery.data ?? [],
      organizationId,
      projectNamesQuery.data ?? {},
    );
  }, [benefitsReady, kaizens, kaizenRoiQuery.data, organizationId, projectNamesQuery.data]);

  const governedBenefits = benefitsReady ? governBenefits(benefitLines) : null;
  const registeredAnnualSavings = governedBenefits?.activeAnnual ?? null;
  const financialWaterfall = useMemo(
    () => registeredAnnualSavings == null
      ? []
      : buildWaterfallSteps(registeredAnnualSavings, metrics, totalThroughput, avgLeadTimeHours, wipStageRows),
    [registeredAnnualSavings, metrics, totalThroughput, avgLeadTimeHours, wipStageRows],
  );
  const resultOriginRows = useMemo(
    () => financialWaterfall.map((step) => ({
      label: step.label,
      amount: step.amount,
      pct: step.pct,
    })),
    [financialWaterfall],
  );
  const gainsRanking = useMemo(
    () => buildGainsRanking(kaizens, financialWaterfall),
    [kaizens, financialWaterfall],
  );
  const savingsByArea = useMemo(
    () => buildSavingsByArea(stagePerformance, registeredAnnualSavings ?? 0),
    [stagePerformance, registeredAnnualSavings],
  );
  const financialSeries = useMemo(
    () => throughputChartData.map((point) => ({ ...point, cumulativeSavings: 0, ratio: 0 })),
    [throughputChartData],
  );
  const savingsTrendPct = null;
  const savingsTrend90Pct = null;
  const totalSavings = registeredAnnualSavings ?? 0;
  const savingsProjection = registeredAnnualSavings;
  const benefitByProject = useMemo(
    () => buildAnnualSavingsByProject(benefitLines.filter((line) => line.inclusion === 'active')),
    [benefitLines],
  );

  const monthlyEvolution = useMemo(
    () => buildMonthlyEvolution(financialImpactData),
    [financialImpactData],
  );

  const consolidatedRoi = useMemo(
    () => governedBenefits
      ? {
          roiPct: governedBenefits.roiPct,
          paybackMonths: governedBenefits.paybackMonths,
          investment: governedBenefits.activeInvestment,
        }
      : { roiPct: null, paybackMonths: null, investment: null },
    [governedBenefits],
  );

  const systemStatus = useMemo(() => {
    const sla = metrics?.sla_rate_7d ?? 0;
    const bottleneckWip = sortedStages[0]?.avg_wip ?? 0;
    const overdueCount = Math.max(teamOverdueCount, overdueExecutions.length, metrics?.tasks_overdue ?? 0);
    const liveStages = stagePerformance.filter((stage) => stage.avg_wip > 0);
    const measuredBreach = liveStages.some((stage) => stage.sla_sampled !== false && stage.sla_rate < 85);
    const queuePressure = liveStages.some((stage) => stage.avg_wip >= 8);

    if (measuredBreach || overdueCount >= 3 || bottleneckWip >= 20) {
      return {
        color: 'bg-[#DC2626]',
        label: 'Problema',
        headline: 'Operação com desvio crítico',
        description: 'SLA pressionado, WIP elevado ou gargalo recorrente exige intervenção imediata.',
      };
    }

    if (queuePressure || sla < 97 || overdueCount > 0 || bottleneckWip >= 10) {
      return {
        color: 'bg-[#D97706]',
        label: 'Atenção',
        headline: 'Operação sob monitoramento',
        description: queuePressure && !measuredBreach
          ? 'Há fila concentrada. Essas etapas ainda não têm conclusão bastante para cravar o SLA.'
          : 'Há sinais de pressão operacional, mas o fluxo ainda está controlado.',
      };
    }

    return {
      color: 'bg-[#16A34A]',
      label: 'Operação normal',
      headline: 'Fluxo dentro do regime esperado',
      description: 'SLA, WIP e gargalos estão dentro da faixa operacional prevista.',
    };
  }, [metrics?.sla_rate_7d, metrics?.tasks_overdue, overdueExecutions.length, sortedStages, stagePerformance, teamOverdueCount]);

  const operationalKpis = useMemo(() => {
    let tpTrend: 'up' | 'down' | 'flat' = 'flat';
    if (throughputChartData.length >= 4) {
      const mid = Math.floor(throughputChartData.length / 2);
      const avgFirst = throughputChartData.slice(0, mid).reduce((s, d) => s + (d.value ?? 0), 0) / mid;
      const avgSecond = throughputChartData.slice(mid).reduce((s, d) => s + (d.value ?? 0), 0) / (throughputChartData.length - mid);
      if (avgSecond > avgFirst + 0.5) tpTrend = 'up';
      else if (avgSecond < avgFirst - 0.5) tpTrend = 'down';
    }

    const arrow = (t: 'up' | 'down' | 'flat') => (t === 'up' ? ' ↑' : t === 'down' ? ' ↓' : '');
    const trendColor = (t: 'up' | 'down' | 'flat', upIsGood: boolean) =>
      t === 'flat' ? '' : (t === 'up') === upIsGood ? 'text-emerald-500' : 'text-red-500';

    const wipCount = myTasks.length > 0
      ? myTasks.length
      : wipStageRows.reduce((sum, stage) => sum + stage.avg_wip, 0);

    const throughputWindow = metrics?.throughput_7d != null ? '7 dias' : `${chartDays}d`;

    return [
      {
        label: `Lead Time · ${chartDays}d`,
        value: hasSeriesActivity(leadTimeChartData) ? `${avgLeadTimeHours.toFixed(1)}h` : '—',
        trendColor: '',
      },
      {
        label: `Throughput · ${throughputWindow}`,
        value: `${metrics?.throughput_7d ?? totalThroughput}${arrow(tpTrend)}`,
        trendColor: trendColor(tpTrend, true),
      },
      {
        label: 'WIP · agora',
        value: String(Math.round(wipCount)),
        trendColor: wipCount > 10 ? 'text-amber-500' : '',
      },
    ];
  }, [
    avgLeadTimeHours,
    chartDays,
    leadTimeChartData,
    metrics?.throughput_7d,
    myTasks.length,
    throughputChartData,
    totalThroughput,
    wipStageRows,
  ]);

  const performanceTrends = useMemo(() => {
    const direction = (
      series: Array<{ value?: number }>,
      threshold: number,
      higherIsBetter: boolean,
    ): 'improving' | 'worsening' | 'stable' | null => {
      if (!hasSeriesActivity(series) || series.length < 4) return null;
      const mid = Math.floor(series.length / 2);
      const avg = (rows: Array<{ value?: number }>) =>
        rows.reduce((sum, row) => sum + (row.value ?? 0), 0) / Math.max(rows.length, 1);
      const delta = avg(series.slice(mid)) - avg(series.slice(0, mid));
      if (Math.abs(delta) <= threshold) return 'stable';
      const improved = higherIsBetter ? delta > 0 : delta < 0;
      return improved ? 'improving' : 'worsening';
    };

    return {
      windowDays: chartDays,
      method: 'Tendência no período: compara a segunda metade da janela com a primeira metade.',
      sla: direction(slaChartData, 2, true),
      throughput: direction(throughputChartData, 0.5, true),
      leadTime: null as 'improving' | 'worsening' | 'stable' | null,
    };
  }, [chartDays, slaChartData, throughputChartData]);

  return {
    chartDays,
    periodDays,
    metrics,
    usersPerformance,
    operationalStages,
    wipStageRows,
    maxStageWip,
    stageWipRows: stageWipQuery.data ?? [],
    slaChartData,
    throughputChartData,
    leadTimeChartData,
    bottleneckChartData,
    pauseChartData,
    financialSeries,
    financialImpactData,
    totalThroughput,
    totalSavings,
    savingsTrendPct,
    savingsTrend90Pct,
    savingsProjection,
    resultOriginRows,
    financialWaterfall,
    gainsRanking,
    savingsByArea,
    savingsByProject,
    benefitsReady,
    benefitLines,
    governedBenefits,
    registeredAnnualSavings,
    benefitByProject,
    monthlyEvolution,
    consolidatedRoi,
    kaizens,
    primaryBottleneck,
    teamPendingCount,
    teamInProgressCount,
    teamOverdueCount,
    systemStatus,
    operationalKpis,
    performanceTrends,
    hasOperationalFlow: operationalStages.length > 0,
    hasWipStageData: wipStageRows.length > 0,
    hasStageWipTable: (stageWipQuery.data?.length ?? 0) > 0,
    hasUsersPerformance: usersPerformance.length > 0,
    hasSlaTrendData: hasSeriesActivity(slaChartData),
    hasThroughputData: hasSeriesActivity(throughputChartData),
    hasLeadTimeData: hasSeriesActivity(leadTimeChartData),
    hasBottleneckData: recurringBottlenecks.some((s) => s.recurrence_score > 0 && s.avg_wip > 0),
    hasPauseData: (pauseParetoQuery.data?.length ?? 0) > 0,
    hasFinancialSeriesData: hasSeriesActivity(financialSeries),
    hasFinancialImpactData: hasSeriesActivity(financialImpactData),
  };
}

export type OpsKaizenData = ReturnType<typeof useOpsKaizenData>;
